/**
 * Flexible onboarding: user picks which sync modules to configure now.
 * Unselected modules can be completed later on Economic / before sync.
 */
export type SyncModuleKey = 'invoice' | 'payment' | 'payout' | 'subscription' | 'refund';

export const ALL_SYNC_MODULES: SyncModuleKey[] = [
    'invoice',
    'refund',
    'payment',
    'payout',
    'subscription',
];

export const needsCommonSetup = (modules: SyncModuleKey[]): boolean =>
    modules.some((m) => m === 'invoice' || m === 'payment' || m === 'subscription' || m === 'refund');

export type WizardStepKey =
    | 'modules'
    | SyncModuleKey
    | 'common'
    | 'success'
    | 'error';

export interface WizardStepDef {
    key: WizardStepKey;
    labelKey: string;
}

/**
 * Build ordered wizard steps from selected modules.
 * Step 0 is always module picker (handled separately when modules empty).
 * Refund reuses invoice settings — no dedicated wizard step.
 */
export const buildWizardSteps = (modules: SyncModuleKey[]): WizardStepDef[] => {
    const selected = ALL_SYNC_MODULES.filter((m) => modules.includes(m));
    const steps: WizardStepDef[] = [
        {key: 'modules', labelKey: 'common.setup.stepModules'},
    ];

    selected.forEach((key) => {
        if (key === 'refund') {
            return;
        }
        const labelMap: Record<Exclude<SyncModuleKey, 'refund'>, string> = {
            invoice: 'common.setup.stepInvoice',
            payment: 'common.setup.stepPayment',
            payout: 'common.setup.stepPayout',
            subscription: 'common.setup.stepSubscription',
        };
        steps.push({key, labelKey: labelMap[key]});
    });

    return steps;
};

export const normalizeEnabledModules = (raw: unknown): SyncModuleKey[] => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.filter((item): item is SyncModuleKey =>
        ALL_SYNC_MODULES.includes(item as SyncModuleKey)
    );
};
